/**
 * MediaSelector Component
 *
 * Shared component for selecting featured or background media (images/videos)
 * Used across all marketing blocks (Hero, Feature, CTA sections)
 *
 * Features:
 * - Upload tab for new images/videos
 * - Gallery selection (future integration with MediaBrowser)
 * - YouTube URL input option
 * - Inline SVG support for vector graphics
 * - Lottie animation JSON support
 * - Preview display
 * - Alt text input for accessibility
 * - Remove functionality
 *
 * Props:
 * - value: Current media object { type, url, alt, svgCode, animationJson }
 * - onChange: Callback when media changes
 * - label: Field label (e.g., "Featured Image", "Background Video")
 * - allowVideo: Boolean to enable video selection
 * - allowYoutube: Boolean to enable YouTube embeds
 * - allowSvg: Boolean to enable inline SVG
 * - allowAnimation: Boolean to enable Lottie animations
 */

import React, { useState, useEffect, useRef } from "react";
import ImageUploadField from "../../shared/ImageUploadField.jsx";

export default function MediaSelector({
  value = null,
  onChange,
  label = "Media",
  allowVideo = true,
  allowYoutube = false,
  allowSvg = false,
  allowAnimation = false,
  isEditMode = true,
}) {
  const [activeTab, setActiveTab] = useState("upload");
  const [svgInput, setSvgInput] = useState(value?.svgCode || "");
  const [animationInput, setAnimationInput] = useState(
    value?.animationJson || "",
  );
  const [svgError, setSvgError] = useState("");
  const [animationError, setAnimationError] = useState("");
  const lottieContainerRef = useRef(null);
  const lottieInstanceRef = useRef(null);

  if (!isEditMode) {
    if (!value || !value.url) return null;

    return (
      <div className="media-preview">
        {value.type === "image" && (
          <img
            src={value.url}
            alt={value.alt || ""}
            className="max-w-full h-auto rounded"
          />
        )}
        {value.type === "video" && value.url.includes("youtube") && (
          <div className="aspect-video">
            <iframe
              src={value.url}
              className="w-full h-full rounded"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        )}
        {value.type === "video" && !value.url.includes("youtube") && (
          <video
            src={value.url}
            controls
            className="max-w-full h-auto rounded"
          />
        )}
      </div>
    );
  }

  const handleImageUpload = (url) => {
    onChange({
      type: "image",
      url: url,
      alt: value?.alt || "",
    });
  };

  const handleAltChange = (e) => {
    if (value) {
      onChange({
        ...value,
        alt: e.target.value,
      });
    }
  };

  const handleRemove = () => {
    onChange(null);
  };

  const handleYoutubeUrl = (e) => {
    const url = e.target.value.trim();
    if (url) {
      // Convert YouTube URL to embed format
      let embedUrl = url;
      if (url.includes("watch?v=")) {
        const videoId = url.split("watch?v=")[1].split("&")[0];
        embedUrl = `https://www.youtube.com/embed/${videoId}`;
      } else if (url.includes("youtu.be/")) {
        const videoId = url.split("youtu.be/")[1].split("?")[0];
        embedUrl = `https://www.youtube.com/embed/${videoId}`;
      }

      onChange({
        type: "video",
        url: embedUrl,
        alt: value?.alt || "",
      });
    }
  };

  const handleSvgSave = () => {
    const code = svgInput.trim();
    if (!code) {
      setSvgError("Please enter SVG code");
      return;
    }

    // Basic validation - check if it's valid SVG
    if (!code.includes("<svg") || !code.includes("</svg>")) {
      setSvgError("Invalid SVG: must contain <svg> tags");
      return;
    }

    // Security: Remove script tags and event handlers
    const sanitizedSvg = code
      .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, "")
      .replace(/on\w+="[^"]*"/gi, "")
      .replace(/on\w+='[^']*'/gi, "");

    setSvgError("");
    onChange({
      type: "svg",
      url: "", // No URL for inline SVG
      svgCode: sanitizedSvg,
      alt: value?.alt || "",
    });
  };

  const handleAnimationSave = () => {
    const jsonStr = animationInput.trim();
    if (!jsonStr) {
      setAnimationError("Please enter animation JSON");
      return;
    }

    try {
      // Validate JSON
      const parsed = JSON.parse(jsonStr);

      // Basic Lottie validation - check for common properties
      if (!parsed.v || !parsed.fr || !parsed.w || !parsed.h) {
        setAnimationError(
          "Invalid Lottie JSON: missing required properties (v, fr, w, h)",
        );
        return;
      }

      setAnimationError("");
      onChange({
        type: "animation",
        url: "", // No URL for inline animation
        animationJson: jsonStr,
        alt: value?.alt || "",
      });
    } catch (e) {
      setAnimationError("Invalid JSON: " + e.message);
    }
  };

  // Effect for Lottie preview
  useEffect(() => {
    if (
      value?.type === "animation" &&
      value?.animationJson &&
      lottieContainerRef.current
    ) {
      // Dynamic import for Lottie (it's a large library)
      import("lottie-web")
        .then((lottie) => {
          if (lottieInstanceRef.current) {
            lottieInstanceRef.current.destroy();
          }

          try {
            lottieInstanceRef.current = lottie.default.loadAnimation({
              container: lottieContainerRef.current,
              renderer: "svg",
              loop: true,
              autoplay: true,
              animationData: JSON.parse(value.animationJson),
            });
          } catch (e) {
            console.error("Failed to load Lottie animation:", e);
          }
        })
        .catch(() => {
          console.warn("Lottie-web not installed. Run: npm install lottie-web");
        });
    }

    return () => {
      if (lottieInstanceRef.current) {
        lottieInstanceRef.current.destroy();
        lottieInstanceRef.current = null;
      }
    };
  }, [value?.type, value?.animationJson]);

  return (
    <div className="media-selector mb-4">
      <label className="block text-sm font-medium text-neutral-700 mb-2">
        {label}
      </label>

      {value && (value.url || value.svgCode || value.animationJson) ? (
        <div className="space-y-2">
          {/* Preview */}
          <div className="relative">
            {value.type === "image" && (
              <img
                src={value.url}
                alt={value.alt || ""}
                className="max-w-full h-auto rounded border"
              />
            )}
            {value.type === "video" && value.url.includes("youtube") && (
              <div className="aspect-video">
                <iframe
                  src={value.url}
                  className="w-full h-full rounded border"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            )}
            {value.type === "svg" && value.svgCode && (
              <div
                className="max-w-full rounded border p-4 bg-gray-50"
                dangerouslySetInnerHTML={{ __html: value.svgCode }}
              />
            )}
            {value.type === "animation" && value.animationJson && (
              <div
                ref={lottieContainerRef}
                className="max-w-full rounded border p-4 bg-gray-50 min-h-[200px]"
              />
            )}
            <button
              type="button"
              onClick={handleRemove}
              className="absolute top-2 right-2 bg-red-600 text-white px-2 py-1 rounded text-xs hover:bg-red-700"
            >
              Remove
            </button>
          </div>

          {/* Alt text input for images and SVGs */}
          {(value.type === "image" || value.type === "svg") && (
            <input
              type="text"
              value={value.alt || ""}
              onChange={handleAltChange}
              placeholder="Alt text (required for accessibility)"
              className="form-control"
              maxLength={200}
            />
          )}
        </div>
      ) : (
        <div className="border rounded p-4">
          {/* Tabs */}
          <div className="flex flex-wrap gap-2 mb-4 border-b">
            <button
              type="button"
              onClick={() => setActiveTab("upload")}
              className={`px-3 py-2 text-sm font-medium ${
                activeTab === "upload"
                  ? "border-b-2 border-blue-500 text-blue-600"
                  : "text-neutral-500 hover:text-neutral-700"
              }`}
            >
              Upload
            </button>
            {allowYoutube && (
              <button
                type="button"
                onClick={() => setActiveTab("youtube")}
                className={`px-3 py-2 text-sm font-medium ${
                  activeTab === "youtube"
                    ? "border-b-2 border-blue-500 text-blue-600"
                    : "text-neutral-500 hover:text-neutral-700"
                }`}
              >
                YouTube
              </button>
            )}
            {allowSvg && (
              <button
                type="button"
                onClick={() => setActiveTab("svg")}
                className={`px-3 py-2 text-sm font-medium ${
                  activeTab === "svg"
                    ? "border-b-2 border-blue-500 text-blue-600"
                    : "text-neutral-500 hover:text-neutral-700"
                }`}
              >
                SVG
              </button>
            )}
            {allowAnimation && (
              <button
                type="button"
                onClick={() => setActiveTab("animation")}
                className={`px-3 py-2 text-sm font-medium ${
                  activeTab === "animation"
                    ? "border-b-2 border-blue-500 text-blue-600"
                    : "text-neutral-500 hover:text-neutral-700"
                }`}
              >
                Animation
              </button>
            )}
          </div>

          {/* Tab content */}
          {activeTab === "upload" && (
            <ImageUploadField
              value={value?.url || ""}
              onUpload={handleImageUpload}
              variant="medium"
            />
          )}

          {activeTab === "youtube" && allowYoutube && (
            <div>
              <input
                type="url"
                onBlur={handleYoutubeUrl}
                placeholder="https://www.youtube.com/watch?v=..."
                className="form-control"
              />
              <p className="text-xs text-neutral-500 mt-1">
                Paste a YouTube video URL
              </p>
            </div>
          )}

          {activeTab === "svg" && allowSvg && (
            <div className="space-y-2">
              <textarea
                value={svgInput}
                onChange={(e) => setSvgInput(e.target.value)}
                placeholder="<svg xmlns='http://www.w3.org/2000/svg' ...>...</svg>"
                className="form-control font-mono text-xs"
                rows={8}
              />
              {svgError && <p className="text-xs text-red-600">{svgError}</p>}
              <div className="flex justify-between items-center">
                <p className="text-xs text-neutral-500">
                  Paste SVG code (scripts and event handlers will be removed for
                  security)
                </p>
                <button
                  type="button"
                  onClick={handleSvgSave}
                  className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                >
                  Apply SVG
                </button>
              </div>
            </div>
          )}

          {activeTab === "animation" && allowAnimation && (
            <div className="space-y-2">
              <textarea
                value={animationInput}
                onChange={(e) => setAnimationInput(e.target.value)}
                placeholder='{"v":"5.7.1","fr":30,"ip":0,"op":60,"w":100,"h":100,...}'
                className="form-control font-mono text-xs"
                rows={8}
              />
              {animationError && (
                <p className="text-xs text-red-600">{animationError}</p>
              )}
              <div className="flex justify-between items-center">
                <p className="text-xs text-neutral-500">
                  Paste Lottie animation JSON (from LottieFiles or After
                  Effects)
                </p>
                <button
                  type="button"
                  onClick={handleAnimationSave}
                  className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                >
                  Apply Animation
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
